// src/pages/HechoNuevo.jsx
//
// Levantar un hecho operativo. Cuatro pasos que siguen el formato oficial en
// papel, pero con la mitad de la captura resuelta: el árbol de decisión de un
// solo nivel y el autocompletado desde el intranet convierten los 8 datos del
// colaborador en 2.
//
// Presentado como un acta —pasos numerados, secciones con reglas, serif en los
// títulos— porque eso es lo que la persona está llenando.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bootstrap, lookupEmployee, createIncident, uploadAttachments } from '../services/incidentApi';
import { HechosStyles, Membrete, I, Pill, Campo } from '../components/hechos/ui';

const PASOS = [
    { n: 1, label: 'A quién le ocurrió' },
    { n: 2, label: 'Herramientas' },
    { n: 3, label: 'Los hechos' },
    { n: 4, label: 'Revisar y enviar' },
];

// ── Catálogo estado → municipios (INEGI) ────────────────────────────────
// El JSON usa los nombres oficiales largos del catálogo AGEEML ("Michoacán de
// Ocampo", "México"); las zonas legales pueden estar nombradas en corto. El
// cruce se hace normalizado (sin acentos, minúsculas) con alias para los dos
// ambiguos, y por prefijo para los demás ("veracruz" → "veracruz de ignacio…").
const normalizar = (s) => String(s || '').toLowerCase()
    .normalize('NFD').replace(/\p{M}/gu, '').trim();

const ALIAS_ESTADO = {
    'cdmx': 'Ciudad de México',
    'distrito federal': 'Ciudad de México',
    'ciudad de mexico': 'Ciudad de México',
    'mexico': 'México',
    'estado de mexico': 'México',
    'edomex': 'México',
    'edo de mexico': 'México',
    'edo. de mexico': 'México',
};

function municipiosDe(catalogo, estado) {
    if (!catalogo || !estado) return null;
    const n = normalizar(estado);
    const porAlias = ALIAS_ESTADO[n];
    if (porAlias && catalogo[porAlias]) return catalogo[porAlias];
    const claves = Object.keys(catalogo);
    const clave = claves.find(k => normalizar(k) === n) || claves.find(k => normalizar(k).startsWith(n));
    return clave ? catalogo[clave] : null;
}

const herramientaVacia = () => ({ asignado: null, marcaModelo: '', placas: '', color: '', serie: '', estatusActual: '' });

export default function HechoNuevo() {
    const navigate = useNavigate();

    const [paso, setPaso]   = useState(1);
    const [boot, setBoot]   = useState(null);
    const [guardando, setGuardando] = useState(false);
    // Catálogo INEGI de municipios. Import dinámico: son ~190 KB que no tienen
    // por qué viajar en el bundle principal de CaprepaDesk.
    const [munCatalogo, setMunCatalogo] = useState(null);

    useEffect(() => {
        import('../data/estadosMunicipios.json')
            .then(m => setMunCatalogo(m.default || m))
            .catch(() => {});   // sin catálogo, el municipio se captura a mano
    }, []);

    // Árbol de decisión: una sola pregunta, dos ramas.
    const [esParaMi, setEsParaMi] = useState(false);
    const [idEmpleado, setIdEmpleado] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [ficha, setFicha] = useState(null);       // del intranet
    const [manual, setManual] = useState(false);    // el ID no existía
    const [duplicados, setDuplicados] = useState([]); // hechos vigentes de ese colaborador
    const [colaborador, setColaborador] = useState({ nombre: '', cargo: '', marca: '', sucursal: '', rutaCobranza: '', gerenteACargo: '' });

    // Documentación solicitada por el tipo de evento, adjuntada desde el alta.
    // El expediente aún no existe, así que los archivos se retienen aquí y se
    // suben (con su docType, para que el pendiente se cierre solo) justo
    // después de que el folio se emite.
    const [docsAdjuntos, setDocsAdjuntos] = useState({});   // docKey → File[]
    const docFileRef = useRef(null);
    const [docKeyActivo, setDocKeyActivo] = useState(null);
    // Guía de clasificación: el catálogo del área legal con su descripción en
    // lenguaje llano, para que el gerente sepa qué tipo elegir.
    const [guiaAbierta, setGuiaAbierta] = useState(false);

    const [form, setForm] = useState({
        eventType: '', fechaDelHecho: '', municipio: '', estado: '', lugarDeLosHechos: '',
        montoDetrimento: '', articulosPersonales: '', telefonoReportante: '',
        narrativeRaw: '', accionesRealizadas: '',
        vehiculoAutoridad: false, vehiculoAutoridadDetalle: '',
    });
    const [herr, setHerr] = useState({
        vehiculo: herramientaVacia(), celular: herramientaVacia(), computadora: herramientaVacia(),
    });

    useEffect(() => {
        bootstrap()
            .then(b => {
                setBoot(b);
                if (!b.perfil?.canReport) {
                    toast.error('Solo puestos de gerencia o superiores pueden levantar un hecho');
                    navigate('/hechos');
                }
            })
            .catch(() => { toast.error('No se pudo abrir el formulario'); navigate('/hechos'); });
    }, [navigate]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setH = (tipo, k, v) => setHerr(h => ({ ...h, [tipo]: { ...h[tipo], [k]: v } }));

    const tipoSel = boot?.tiposEvento?.find(t => t._id === form.eventType);
    // Solo lo que le toca reunir al reportante: lo que gestiona RH no es parte
    // de su flujo (vive como pendiente en el expediente), y los documentos
    // condicionados —folio y ubicación del corralón— solo aplican si el hecho
    // dejó un vehículo con la autoridad.
    const docsRequeridos = (tipoSel?.requiredDocs || []).filter(d =>
        d.owner !== 'rh' &&
        (!d.condicion || (d.condicion === 'vehiculo_autoridad' && form.vehiculoAutoridad)));

    function cambiarTipo(eventType) {
        set('eventType', eventType);
        // Al cambiar de tipo se conservan solo los adjuntos cuyos documentos
        // sigan solicitados; los del tipo anterior no viajan por error.
        const claves = new Set((boot?.tiposEvento?.find(t => t._id === eventType)?.requiredDocs || []).map(d => d.key));
        setDocsAdjuntos(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => claves.has(k))));
    }

    function pedirDoc(docKey) {
        setDocKeyActivo(docKey);
        docFileRef.current?.click();
    }

    function agregarDocs(files) {
        if (!files?.length || !docKeyActivo) return;
        setDocsAdjuntos(prev => ({ ...prev, [docKeyActivo]: [...(prev[docKeyActivo] || []), ...files] }));
    }

    function quitarDoc(docKey, idx) {
        setDocsAdjuntos(prev => ({ ...prev, [docKey]: (prev[docKey] || []).filter((_, i) => i !== idx) }));
    }

    async function buscarEmpleado() {
        const id = idEmpleado.trim();
        if (!id) return;
        setBuscando(true); setManual(false);
        try {
            const card = await lookupEmployee(id);
            // Intranet caído: el backend responde 200 con manual:true en vez de
            // 500, para no atorar el alta. Se cae a captura manual, igual que un
            // empleado no encontrado.
            if (card.manual) {
                setFicha(null); setManual(true);
                toast.info(card.aviso || 'Sin registro en el intranet: captura los datos manualmente');
                return;
            }
            setFicha(card);
            setDuplicados(card.hechosAbiertos || []);
            setColaborador(c => ({
                ...c,
                nombre: card.nombre, cargo: card.cargo, marca: card.marca,
                sucursal: card.sucursal, municipioEstado: card.pais,
            }));
        } catch (e) {
            if (e.response?.status === 404) {
                // Personal nuevo, tercero, externo o dado de baja. Nunca
                // bloqueamos el levantamiento: se cae a captura manual, y si
                // es una baja el servidor lo dice con nombre y fecha.
                setFicha(null); setManual(true);
                if (e.response.data?.baja) toast.warn(e.response.data.error);
                else toast.info('Sin registro en el intranet: captura los datos manualmente');
            } else {
                toast.error('No se pudo consultar el intranet');
            }
        } finally { setBuscando(false); }
    }

    function validar() {
        if (!esParaMi && !colaborador.nombre) return 'Indica a qué colaborador le ocurrió el hecho';
        if (!form.fechaDelHecho) return 'Falta la fecha del hecho';
        if (!form.narrativeRaw.trim()) return 'Falta el detalle de los hechos';
        if (form.telefonoReportante && form.telefonoReportante.length !== 10)
            return 'El celular debe tener 10 dígitos, o déjalo vacío';
        return null;
    }

    async function enviar() {
        const error = validar();
        if (error) { toast.warn(error); return; }
        setGuardando(true);
        try {
            const payload = {
                esParaMi,
                eventType: form.eventType || null,
                fechaDelHecho: form.fechaDelHecho,
                municipio: form.municipio,
                estado: form.estado,
                lugarDeLosHechos: form.lugarDeLosHechos,
                montoDetrimento: form.montoDetrimento === '' ? null : Number(form.montoDetrimento),
                articulosPersonales: form.articulosPersonales,
                telefonoReportante: form.telefonoReportante,
                narrativeRaw: form.narrativeRaw,
                accionesRealizadas: form.accionesRealizadas,
                rutaCobranza: colaborador.rutaCobranza,
                colaboradores: esParaMi ? [] : [{
                    idEmpleado: idEmpleado ? Number(idEmpleado) : null,
                    nombre: colaborador.nombre, cargo: colaborador.cargo, marca: colaborador.marca,
                    sucursal: colaborador.sucursal, municipioEstado: colaborador.municipioEstado || '',
                    rutaCobranza: colaborador.rutaCobranza, gerenteACargo: colaborador.gerenteACargo,
                    desdeIntranet: !!ficha,
                }],
                herramientas: herr,
                vehiculoEnPoderAutoridad: {
                    aplica: form.vehiculoAutoridad,
                    detalle: form.vehiculoAutoridadDetalle,
                },
            };
            const r = await createIncident(payload);
            toast.success(`Folio ${r.folio} emitido`);

            // El expediente ya existe: ahora sí, la documentación adjuntada se
            // sube con su docType para que cada pendiente se cierre solo. Si
            // algo falla, el pendiente queda abierto y se sube después — el
            // levantamiento nunca se pierde por un archivo.
            const porSubir = Object.entries(docsAdjuntos).filter(([, files]) => files.length);
            let fallidos = 0;
            for (const [docKey, files] of porSubir) {
                try { await uploadAttachments(r._id, files, docKey); }
                catch { fallidos++; }
            }
            if (porSubir.length && !fallidos) toast.success('Documentación cargada al expediente');
            if (fallidos) toast.warn(`No se pudo subir la documentación de ${fallidos} ${fallidos === 1 ? 'punto' : 'puntos'}; súbela desde el expediente`);

            navigate(`/hechos/${r._id}`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo levantar el hecho');
        } finally { setGuardando(false); }
    }

    if (!boot) return <div className="ho-wrap"><HechosStyles /><div className="ho-empty">Abriendo el formulario…</div></div>;

    return (
        <div className="ho-wrap ho-narrow">
            <HechosStyles />
            <Membrete />

            <div className="ho-head" style={{ borderBottomWidth: 2, paddingTop: 18 }}>
                <div className="ho-kicker">Nuevo</div>
                <h1 className="ho-h1">Levantar un hecho operativo</h1>
                <div className="ho-head-sub">
                    <span>Reporta <b>{boot.perfil.nombre}</b></span>
                    {boot.perfil.puesto && <span>{boot.perfil.puesto}</span>}
                    {boot.perfil.sucursal && <span>{boot.perfil.sucursal}</span>}
                </div>
            </div>

            {/* Pasos */}
            <div className="d-flex gap-4 flex-wrap" style={{ padding: '22px 0 14px', borderBottom: '1px solid var(--c-border)' }}>
                {PASOS.map(p => (
                    <button key={p.n} onClick={() => setPaso(p.n)}
                            className="btn btn-link p-0 text-start"
                            style={{
                                textDecoration: 'none',
                                color: paso === p.n ? 'var(--ho-ink)' : 'var(--c-text-3)',
                                borderBottom: paso === p.n ? '2px solid var(--ho-ink)' : '2px solid transparent',
                                paddingBottom: 8,
                            }}>
                        <div className="ho-mono" style={{ fontSize: 11, letterSpacing: '.1em' }}>PASO 0{p.n}</div>
                        <div className="ho-serif" style={{ fontSize: 16 }}>{p.label}</div>
                    </button>
                ))}
            </div>

            {/* ── Paso 1 ─────────────────────────────────────────────── */}
            {paso === 1 && (
                <section style={{ paddingTop: 26 }}>
                    <div className="ho-kicker">Sección primera</div>
                    <h2 className="ho-h2" style={{ margin: '6px 0 18px' }}>¿A quién le ocurrió el hecho?</h2>

                    <div className="row g-3">
                        {[
                            { v: true,  t: 'A mí', d: 'Tus datos salen de tu perfil del intranet. No capturas nada.' },
                            { v: false, t: 'A un colaborador', d: 'Con su número de empleado traemos la ficha completa.' },
                        ].map(o => (
                            <div className="col-md-6" key={String(o.v)}>
                                <button className="ho-opt" aria-pressed={esParaMi === o.v}
                                        onClick={() => { setEsParaMi(o.v); setDuplicados([]); setFicha(null); setManual(false); }}>
                                    <div className="ho-serif" style={{ fontSize: 19 }}>{o.t}</div>
                                    <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginTop: 6 }}>{o.d}</div>
                                </button>
                            </div>
                        ))}
                    </div>

                    {!esParaMi && (
                        <div style={{ marginTop: 22 }}>
                            <div className="d-flex gap-2 align-items-end flex-wrap">
                                <div style={{ width: 200 }}>
                                    <label className="ho-campo-k d-block mb-1">Número de empleado</label>
                                    <input className="form-control ho-mono" value={idEmpleado}
                                           onChange={e => setIdEmpleado(e.target.value)}
                                           onKeyDown={e => e.key === 'Enter' && buscarEmpleado()} />
                                </div>
                                <button className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                                        onClick={buscarEmpleado} disabled={buscando}>
                                    {buscando ? 'Consultando intranet…' : <>Consultar intranet {I.arrow}</>}
                                </button>
                            </div>

                            {/* Antiduplicidad: este colaborador ya tiene hechos vigentes */}
                            {duplicados.length > 0 && (
                                <div className="ho-focus" style={{ marginTop: 16 }}>
                                    <span style={{ color: 'var(--ho-pend)', marginTop: 1 }}>{I.alert}</span>
                                    <div style={{ flex: 1 }}>
                                        <div className="t">
                                            Este colaborador ya tiene {duplicados.length === 1 ? 'un hecho vigente' : `${duplicados.length} hechos vigentes`}
                                        </div>
                                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {duplicados.map(h => (
                                                <div key={h._id} style={{ fontSize: 13, color: 'var(--c-text-2)' }}>
                                                    <span className="ho-mono" style={{ color: 'var(--ho-accent)' }}>{h.folio}</span>
                                                    {' — '}{h.tipo}{h.sucursal ? ` · ${h.sucursal}` : ''}
                                                    {' · '}{new Date(h.fecha).toLocaleDateString('es-MX')}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="d" style={{ marginTop: 6 }}>
                                            Si lo que vas a reportar es <strong>ese mismo hecho</strong>, no lo levantes de nuevo —
                                            súbele documentos al expediente que ya existe. Continúa solo si es un hecho <strong>distinto</strong>.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {ficha && (
                                <div style={{
                                    marginTop: 18, padding: '18px 0',
                                    borderTop: '1px solid var(--c-border)', borderBottom: '1px solid var(--c-border)',
                                }}>
                                    <div className="d-flex align-items-center gap-2 mb-3" style={{ color: 'var(--ho-accent)' }}>
                                        {I.check}
                                        <span className="ho-kicker" style={{ color: 'var(--ho-accent)' }}>
                                            Ficha recuperada · 5 campos autocompletados
                                            {ficha.hechosPrevios > 0 && ` · ${ficha.hechosPrevios} hecho${ficha.hechosPrevios > 1 ? 's' : ''} previo${ficha.hechosPrevios > 1 ? 's' : ''} en su historial`}
                                        </span>
                                    </div>
                                    <div className="ho-kv">
                                        <Campo label="Nombre">{ficha.nombre}</Campo>
                                        <Campo label="Cargo">{ficha.cargo}</Campo>
                                        <Campo label="Marca">{ficha.marca}</Campo>
                                        <Campo label="Sucursal">{ficha.sucursal}</Campo>
                                        <Campo label="País">{ficha.pais}</Campo>
                                    </div>
                                    <div style={{ marginTop: 16, maxWidth: 280 }}>
                                        <label className="ho-campo-k d-block mb-1">Ruta de cobranza · único campo manual</label>
                                        <input className="form-control ho-mono" value={colaborador.rutaCobranza}
                                               onChange={e => setColaborador(c => ({ ...c, rutaCobranza: e.target.value }))} />
                                    </div>
                                </div>
                            )}

                            {manual && (
                                <div style={{ marginTop: 18 }}>
                                    <Pill tone="pend" icon={I.alert}>Sin registro en el intranet — captura manual</Pill>
                                    <div className="row g-3 mt-1">
                                        {[['nombre', 'Nombre completo'], ['cargo', 'Cargo'], ['marca', 'Marca'],
                                          ['sucursal', 'Sucursal'], ['rutaCobranza', 'Ruta de cobranza'],
                                          ['gerenteACargo', 'Gerente o zonal a cargo']].map(([k, l]) => (
                                            <div className="col-md-6" key={k}>
                                                <label className="ho-campo-k d-block mb-1">{l}</label>
                                                <input className="form-control" value={colaborador[k] || ''}
                                                       onChange={e => setColaborador(c => ({ ...c, [k]: e.target.value }))} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 14 }}>
                                Los demás datos que pide el formato oficial se completan automáticamente desde el intranet.
                            </p>
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <button className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={() => setPaso(2)}>Continuar {I.arrow}</button>
                    </div>
                </section>
            )}

            {/* ── Paso 2 ─────────────────────────────────────────────── */}
            {paso === 2 && (
                <section style={{ paddingTop: 26 }}>
                    <div className="ho-kicker">Sección segunda</div>
                    <h2 className="ho-h2" style={{ margin: '6px 0 6px' }}>Herramientas de trabajo asignadas</h2>
                    <p style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
                        Marca <strong>No</strong> cuando no la tenía asignada o no aplique: no se cuenta como dato faltante.
                    </p>

                    {[
                        { k: 'vehiculo',    label: 'Vehículo',    extra: true },
                        { k: 'celular',     label: 'Celular',     extra: false },
                        { k: 'computadora', label: 'Computadora', extra: false },
                    ].map(({ k, label, extra }) => (
                        <div key={k} style={{ padding: '18px 0', borderBottom: '1px solid var(--c-border)' }}>
                            <div className="row g-3 align-items-start">
                                <div className="col-md-3"><div className="ho-serif" style={{ fontSize: 17 }}>{label}</div></div>
                                <div className="col-md-9">
                                    <div className="btn-group btn-group-sm" role="group">
                                        {[['si', 'Sí'], ['no', 'No']].map(([v, t]) => (
                                            <button key={v} type="button"
                                                    className={`btn ${herr[k].asignado === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    onClick={() => setH(k, 'asignado', v)}>{t}</button>
                                        ))}
                                    </div>

                                    {herr[k].asignado === 'si' && (
                                        <div className="row g-2 mt-2">
                                            <div className={extra ? 'col-md-4' : 'col-md-6'}>
                                                <label className="ho-campo-k d-block mb-1">Marca y modelo</label>
                                                <input className="form-control form-control-sm" value={herr[k].marcaModelo}
                                                       onChange={e => setH(k, 'marcaModelo', e.target.value)} />
                                            </div>
                                            {extra && (
                                                <>
                                                    <div className="col-md-4">
                                                        <label className="ho-campo-k d-block mb-1">Placas</label>
                                                        <input className="form-control form-control-sm ho-mono" value={herr[k].placas}
                                                               onChange={e => setH(k, 'placas', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="ho-campo-k d-block mb-1">Color</label>
                                                        <input className="form-control form-control-sm" value={herr[k].color}
                                                               onChange={e => setH(k, 'color', e.target.value)} />
                                                    </div>
                                                </>
                                            )}
                                            <div className={extra ? 'col-12' : 'col-md-6'}>
                                                <label className="ho-campo-k d-block mb-1">Estatus al momento del reporte</label>
                                                <input className="form-control form-control-sm" value={herr[k].estatusActual}
                                                       onChange={e => setH(k, 'estatusActual', e.target.value)} />
                                            </div>
                                        </div>
                                    )}
                                    {herr[k].asignado === 'no' && (
                                        <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', margin: '10px 0 0' }}>
                                            No se solicitará información adicional.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="d-flex justify-content-between mt-4">
                        <button className="btn btn-outline-secondary" onClick={() => setPaso(1)}>Atrás</button>
                        <button className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={() => setPaso(3)}>Continuar {I.arrow}</button>
                    </div>
                </section>
            )}

            {/* ── Paso 3 ─────────────────────────────────────────────── */}
            {paso === 3 && (
                <section style={{ paddingTop: 26 }}>
                    <div className="ho-kicker">Sección tercera</div>
                    <h2 className="ho-h2" style={{ margin: '6px 0 18px' }}>Descripción de los hechos</h2>

                    {/* ── Cuándo y dónde ─────────────────────────────── */}
                    <div className="ho-fs first">
                        <div className="ho-fs-title">Cuándo y dónde</div>
                        <p className="ho-fs-sub">Del estado que elijas depende qué abogado recibe el caso.</p>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="ho-lbl">¿Cuándo ocurrió? <span className="req">*</span></label>
                                <input type="datetime-local" className="form-control" value={form.fechaDelHecho}
                                       onChange={e => set('fechaDelHecho', e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="ho-lbl">¿En qué estado?</label>
                                {/* Catálogo cerrado: el match estado→zona del
                                    enrutamiento es exacto. Cambiarlo limpia el
                                    municipio para que no queden cruzados. */}
                                <select className="form-select" value={form.estado}
                                        onChange={e => setForm(f => ({ ...f, estado: e.target.value, municipio: '' }))}>
                                    <option value="">— selecciona —</option>
                                    {(boot.estadosCatalogo?.length
                                        ? boot.estadosCatalogo
                                        : Object.keys(munCatalogo || {})
                                    ).map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="ho-lbl">¿En qué municipio?</label>
                                {(() => {
                                    const municipios = municipiosDe(munCatalogo, form.estado);
                                    return municipios?.length ? (
                                        <select className="form-select" value={form.municipio}
                                                onChange={e => set('municipio', e.target.value)}>
                                            <option value="">— selecciona —</option>
                                            {municipios.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    ) : (
                                        <input className="form-control" value={form.municipio}
                                               onChange={e => set('municipio', e.target.value)}
                                               placeholder={form.estado ? 'Escribe el municipio' : 'Primero elige el estado'} />
                                    );
                                })()}
                            </div>
                            <div className="col-12">
                                <label className="ho-lbl">Lugar exacto</label>
                                <input className="form-control" value={form.lugarDeLosHechos}
                                       onChange={e => set('lugarDeLosHechos', e.target.value)}
                                       placeholder="Calle y colonia, la sucursal o el punto de la ruta donde pasó" />
                            </div>
                        </div>
                    </div>

                    {/* ── Qué pasó ───────────────────────────────────── */}
                    <div className="ho-fs">
                        <div className="ho-fs-title">Qué pasó</div>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="ho-lbl">¿Qué tipo de evento fue?
                                    <small>Si no estás seguro, déjalo sin clasificar: el abogado lo confirma.</small>
                                </label>
                                <select className="form-select" value={form.eventType} onChange={e => cambiarTipo(e.target.value)}>
                                    <option value="">Sin clasificar</option>
                                    {boot.tiposEvento.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                                </select>
                                {/* El tipo elegido, resaltado: entre tanto campo, la
                                    clasificación es el dato que ordena todo lo demás. */}
                                {tipoSel && (
                                    <div style={{
                                        marginTop: 10, padding: '11px 14px', borderRadius: 6,
                                        background: 'color-mix(in srgb, var(--ho-accent) 7%, var(--c-card))',
                                        border: '1px solid color-mix(in srgb, var(--ho-accent) 30%, transparent)',
                                        boxShadow: 'inset 3px 0 0 var(--ho-accent)',
                                    }}>
                                        <div className="ho-kicker" style={{ color: 'var(--ho-accent)' }}>Clasificado como</div>
                                        <div className="ho-serif" style={{ fontSize: 18, color: 'var(--ho-ink)', marginTop: 2 }}>
                                            {tipoSel.name}
                                        </div>
                                        {tipoSel.description && (
                                            <p style={{ fontSize: 12.5, color: 'var(--c-text-2)', margin: '6px 0 0', lineHeight: 1.55 }}>
                                                {tipoSel.description}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label className="ho-lbl">¿Cuánto dinero perdió la empresa?
                                    <small>Aproximado, en pesos. Si no hubo pérdida o todavía no se sabe, déjalo vacío.</small>
                                </label>
                                <input type="number" className="form-control ho-mono" value={form.montoDetrimento}
                                       onChange={e => set('montoDetrimento', e.target.value)} placeholder="$ 0" />
                            </div>
                            <div className="col-12">
                                <button type="button" className="ho-chip" aria-pressed={guiaAbierta}
                                        onClick={() => setGuiaAbierta(v => !v)}>
                                    {guiaAbierta ? 'Ocultar la guía' : '¿Cuál elijo? Ver la guía de clasificación'}
                                </button>
                                {guiaAbierta && (
                                    <div style={{ marginTop: 10, border: '1px solid var(--c-border)', borderRadius: 6, padding: '2px 16px' }}>
                                        {boot.tiposEvento.map((t, i) => (
                                            <button key={t._id} type="button"
                                                    className="w-100 text-start"
                                                    style={{
                                                        background: 'transparent', border: 0, cursor: 'pointer',
                                                        padding: '11px 0', color: 'inherit', font: 'inherit',
                                                        borderBottom: i === boot.tiposEvento.length - 1 ? 'none' : '1px solid var(--c-border)',
                                                    }}
                                                    onClick={() => { cambiarTipo(t._id); setGuiaAbierta(false); }}>
                                                <span style={{ fontSize: 13.5, fontWeight: 600, color: form.eventType === t._id ? 'var(--ho-accent)' : 'var(--c-text-1)' }}>
                                                    {t.name}
                                                </span>
                                                {t.description && (
                                                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--c-text-2)', lineHeight: 1.55, marginTop: 2 }}>
                                                        {t.description}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="col-12">
                                <label className="ho-lbl">¿El colaborador perdió algo suyo?</label>
                                <input className="form-control" value={form.articulosPersonales}
                                       onChange={e => set('articulosPersonales', e.target.value)}
                                       placeholder="Celular propio, dinero en efectivo, pertenencias… (opcional)" />
                            </div>
                            <div className="col-12">
                                <span className="ho-lbl">¿Algún vehículo quedó en poder de la autoridad (corralón, MP)?</span>
                                <div className="d-flex gap-2 mt-1">
                                    <button type="button" className="ho-chip" aria-pressed={form.vehiculoAutoridad}
                                            onClick={() => set('vehiculoAutoridad', true)}>Sí</button>
                                    <button type="button" className="ho-chip" aria-pressed={!form.vehiculoAutoridad}
                                            onClick={() => set('vehiculoAutoridad', false)}>No</button>
                                </div>
                                {form.vehiculoAutoridad && (
                                    <input className="form-control mt-2" value={form.vehiculoAutoridadDetalle}
                                           onChange={e => set('vehiculoAutoridadDetalle', e.target.value)}
                                           placeholder="¿Cuál y dónde quedó? Por ejemplo: moto de la ruta 12, corralón de Ecatepec" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Documentación solicitada por el tipo de evento ──
                        Se muestra en cuanto el gerente clasifica el hecho:
                        así se entera aquí de qué papeles pide el protocolo,
                        no hasta abrir el expediente. Adjuntar es opcional —
                        el levantamiento nunca se detiene por un documento. */}
                    {docsRequeridos.length > 0 && (
                        <div className="ho-fs">
                            <div className="ho-fs-title">Documentación solicitada</div>
                            <p className="ho-fs-sub">
                                El protocolo pide estos documentos para este tipo de evento. Adjunta lo que ya
                                tengas; lo demás quedará como pendiente en el expediente y podrás subirlo después.
                            </p>
                                {docsRequeridos.map(d => (
                                    <div key={d.key} className="d-flex align-items-center gap-2 flex-wrap"
                                         style={{ padding: '9px 0', borderBottom: '1px solid var(--c-border)' }}>
                                        <div style={{ flex: 1, minWidth: 220 }}>
                                            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{d.label}</span>
                                            {!d.blocking && <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}> · no bloquea</span>}
                                            {d.owner === 'legal' && <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}> · lo gestiona legal</span>}
                                            {d.owner === 'rh' && <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}> · lo gestiona RH</span>}
                                            {(docsAdjuntos[d.key] || []).length > 0 && (
                                                <span className="ho-files" style={{ marginTop: 6 }}>
                                                    {(docsAdjuntos[d.key] || []).map((f, i) => (
                                                        <span key={i} className="ho-file" style={{ cursor: 'default' }}>
                                                            {I.doc} {f.name}
                                                            <button className="btn btn-link p-0 ms-1" style={{ color: 'inherit', lineHeight: 1 }}
                                                                    onClick={() => quitarDoc(d.key, i)} title="Quitar">×</button>
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                        </div>
                                        <button type="button"
                                                className="ho-chip d-inline-flex align-items-center gap-1"
                                                onClick={() => pedirDoc(d.key)}>
                                            {I.clip} Adjuntar
                                        </button>
                                    </div>
                                ))}
                            <input ref={docFileRef} type="file" multiple hidden
                                   onChange={e => { agregarDocs([...e.target.files]); e.target.value = ''; }} />
                        </div>
                    )}

                    {/* ── El relato ──────────────────────────────────── */}
                    <div className="ho-fs">
                        <div className="ho-fs-title">El relato</div>
                        <p className="ho-fs-sub">
                            Escríbelo con tus palabras, como se lo contarías a un compañero. El sistema lo ordena
                            después y un abogado valida la versión oficial — tu texto original nunca se modifica.
                        </p>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="ho-lbl">Cuéntanos qué pasó <span className="req">*</span></label>
                                <textarea className="form-control" rows={8} value={form.narrativeRaw}
                                          onChange={e => set('narrativeRaw', e.target.value)}
                                          placeholder="Qué pasó, a qué hora, en dónde y quiénes estuvieron involucrados. Entre más detalle, mejor." />
                            </div>
                            <div className="col-12">
                                <label className="ho-lbl">¿Qué medidas han tomado desde el suceso?</label>
                                <textarea className="form-control" rows={3} value={form.accionesRealizadas}
                                          onChange={e => set('accionesRealizadas', e.target.value)}
                                          placeholder="Llamadas, reporte a la policía, apoyo al colaborador, aviso a la sucursal… (opcional)" />
                            </div>
                            <div className="col-md-5">
                                <label className="ho-lbl">Número de contacto</label>
                                <input className="form-control" value={form.telefonoReportante}
                                       inputMode="numeric" autoComplete="tel-national"
                                       onChange={e => set('telefonoReportante', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                       placeholder="10 dígitos" />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                        <button className="btn btn-outline-secondary" onClick={() => setPaso(2)}>Atrás</button>
                        <button className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={() => setPaso(4)}>Continuar {I.arrow}</button>
                    </div>
                </section>
            )}

            {/* ── Paso 4: revisar y enviar ───────────────────────────── */}
            {paso === 4 && (
                <section style={{ paddingTop: 26 }}>
                    <div className="ho-kicker">Sección cuarta</div>
                    <h2 className="ho-h2" style={{ margin: '6px 0 18px' }}>Revisar y enviar</h2>

                    {/* Resumen de lo capturado — sin destinatarios: el reporte
                        se envía solo a quien corresponde según la marca. */}
                    <div className="ho-kv">
                        <Campo label="A quién le ocurrió">
                            {esParaMi ? 'A mí' : (colaborador.nombre || 'Colaborador')}
                        </Campo>
                        <Campo label="Tipo de evento">
                            {boot.tiposEvento.find(t => t._id === form.eventType)?.name || 'Sin clasificar'}
                        </Campo>
                        <Campo label="Fecha del hecho">{form.fechaDelHecho || '—'}</Campo>
                        <Campo label="Lugar">
                            {form.lugarDeLosHechos || [form.municipio, form.estado].filter(Boolean).join(', ') || '—'}
                        </Campo>
                        <Campo label="Monto del detrimento">
                            {form.montoDetrimento ? `$${Number(form.montoDetrimento).toLocaleString('es-MX')}` : 'N/A'}
                        </Campo>
                        <Campo label="Artículos personales">{form.articulosPersonales || 'N/A'}</Campo>
                        {docsRequeridos.length > 0 && (() => {
                            const conArchivo = docsRequeridos.filter(d => (docsAdjuntos[d.key] || []).length).length;
                            const total = Object.values(docsAdjuntos).reduce((a, f) => a + f.length, 0);
                            return (
                                <Campo label="Documentación solicitada">
                                    {total > 0
                                        ? `${total} ${total === 1 ? 'archivo' : 'archivos'} — cubres ${conArchivo} de ${docsRequeridos.length} puntos`
                                        : `Sin adjuntar — los ${docsRequeridos.length} puntos quedarán como pendientes del expediente`}
                                </Campo>
                            );
                        })()}
                    </div>

                    <div style={{ borderTop: '2px solid var(--ho-ink)', paddingTop: 18, marginTop: 22 }}>
                        <div className="ho-kicker" style={{ marginBottom: 10 }}>Al enviar</div>
                        {[
                            'Se emite el folio de incidencia',
                            'Se genera el PDF con el formato oficial y el relato original anexo',
                            'Se asigna al abogado de la zona según el estado del hecho',
                            'Se turna al área correspondiente',
                        ].map(t => (
                            <div key={t} className="d-flex align-items-start gap-2 py-1" style={{ fontSize: 13.5 }}>
                                <span style={{ color: 'var(--ho-ok)', marginTop: 2 }}>{I.check}</span>{t}
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                        <button className="btn btn-outline-secondary" onClick={() => setPaso(3)}>Atrás</button>
                        <button className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={enviar} disabled={guardando}>
                            {I.send} {guardando ? 'Emitiendo folio…' : 'Emitir folio y enviar'}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}
